import type { CSSProperties, ReactNode } from 'react';
import { cn, Tooltip } from '@skupreme/shared/ui';
import { toPlacement } from '@utility/popup-placement';

export interface DarkPopupProps {
  /** What it describes: an icon, text, a disabled control. */
  trigger?: ReactNode;
  children?: ReactNode;
  /** Used when there are no children. */
  content?: ReactNode;
  /** A Semantic position such as 'top left'; falsy means the top. */
  position?: string | false;
  /** Classes for the bubble, as Semantic put them on the popup. */
  className?: string;
  contentWidth?: number;
  contentMaxHight?: string | number;
  padding?: string;
  /** Near-black instead of the default dark grey. */
  primaryDark?: boolean;
  /** Told when it opens and closes. */
  setOpen?: (open: boolean) => void;
  /** Never shows while true, as Semantic's `disabled`. */
  disabled?: boolean;
  /** Holds it open, as Semantic's `open`. */
  open?: boolean;
  /**
   * Semantic props callers still pass. A React Aria tooltip already stays
   * open while hovered, and places itself, so these change nothing.
   */
  hoverable?: boolean;
  hover?: boolean;
  offset?: unknown;
  pinned?: boolean;
  on?: unknown;
  wide?: boolean | string;
  basic?: boolean;
  /** Applied to the content box; Semantic's top/right nudges have no effect here. */
  style?: CSSProperties;
  /** Classes for the box around `trigger`, e.g. `sk:flex sk:w-full` for a full-width button. */
  triggerClassName?: string;
}

type PopupTooltipProps = DarkPopupProps & { tone: 'dark' | 'light' };

function PopupTooltip({
  tone,
  trigger,
  children,
  content,
  position,
  className,
  contentWidth,
  contentMaxHight,
  padding,
  primaryDark = false,
  setOpen,
  disabled,
  open,
  style,
  triggerClassName,
}: PopupTooltipProps) {
  const fixedWidth = !!contentWidth && contentWidth > 0;

  return (
    <Tooltip
      placement={toPlacement(position)}
      onOpenChange={setOpen}
      isDisabled={disabled}
      isOpen={open}
      triggerClassName={triggerClassName}
      className={cn(
        'sk:p-0',
        tone === 'dark' && [
          'sk:rounded-[5px] sk:text-white sk:shadow-none',
          primaryDark ? 'sk:bg-primary' : 'sk:bg-primary',
        ],
        fixedWidth && 'sk:max-w-none',
        className,
      )}
      content={
        <div
          className="sk:overflow-x-hidden sk:overflow-y-auto"
          style={{
            padding: padding ?? (tone === 'dark' ? '1rem' : '0.833em 1em'),
            maxHeight:
              contentMaxHight || (tone === 'dark' ? '350px' : undefined),
            ...(fixedWidth
              ? { minWidth: contentWidth, maxWidth: contentWidth }
              : {}),
            ...style,
          }}
        >
          {children ?? content}
        </div>
      }
    >
      {trigger}
    </Tooltip>
  );
}

/**
 * A dark tooltip over `trigger`, on hover and keyboard focus. It keeps the
 * legacy dark look, since much of its content is written for it.
 */
export function DarkPopup(props: DarkPopupProps) {
  return <PopupTooltip {...props} tone="dark" />;
}

/** The same tooltip in the kit's light look, as Semantic's plain Popup was. */
export function LightPopup(props: DarkPopupProps) {
  return <PopupTooltip {...props} tone="light" />;
}

interface SimpleDarkToolTipProps extends Omit<DarkPopupProps, 'children'> {
  helpText?: ReactNode;
  helpPosition?: DarkPopupProps['position'];
}

export function SimpleDarkToolTip({
  helpText = '',
  helpPosition = 'bottom center',
  trigger = <></>,
  ...rest
}: SimpleDarkToolTipProps) {
  if (!helpText) {
    return trigger;
  }

  return (
    <DarkPopup trigger={trigger} position={helpPosition} {...rest}>
      {helpText}
    </DarkPopup>
  );
}
